import React, { useEffect, useState } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDriverPointsSummary } from '../../services/rewardsService';
import toast from 'react-hot-toast';

export default function DriverRewards() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getDriverPointsSummary()
      .then((d) => { if (active) setData(d); })
      .catch((e) => toast.error(e.message || 'Could not load rewards'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <div className="p-[60px] text-center text-muted">Loading rewards...</div>;
  if (!data) return <div className="p-[60px] text-center text-muted">No rewards data available.</div>;

  const { summary, ledger, financialYear } = data;
  const totalPoints = summary?.totalPoints || 0;
  const displayPoints = Math.max(0, totalPoints); // negative totals shown as 0 (clamp matches pool math)

  return (
    <div className="max-w-[700px] mx-auto px-6 py-8">
      <Link to="/driver" className="text-brand-primary font-semibold text-[0.9rem] mb-6 block">← Dashboard</Link>
      <h1 className="font-serif text-[1.8rem] mb-1">My Rewards</h1>
      <div className="text-[0.85rem] text-muted mb-7">Financial Year {financialYear}</div>

      {/* Headline points card */}
      <div className="rounded-2xl p-7 text-center mb-5" style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)', color: '#0a1628' }}>
        <div className="text-[0.85rem] font-semibold opacity-70 mb-1">Total Points This FY</div>
        <div className="text-[3rem] font-extrabold leading-none">{displayPoints}</div>
        {totalPoints < 0 && (
          <div className="text-[0.78rem] mt-2 font-semibold" style={{ color: '#dc2626' }}>
            Ledger balance: {totalPoints} (clamped to 0 for distribution)
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <BreakdownCard label="From trips" value={summary?.tripPoints || 0} sub={`${summary?.tripCount || 0} trip${(summary?.tripCount || 0) !== 1 ? 's' : ''}`} />
        <BreakdownCard label="From referrals" value={summary?.referralPoints || 0} sub={`+$${(summary?.referralBonusEarnedAUD || 0).toFixed(2)} earned`} />
        <BreakdownCard label="Cancellation penalties" value={`-${summary?.cancellationPenalties || 0}`} sub="Late driver cancels" negative />
      </div>

      {/* How points work */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <h3 className="mt-0 mb-3 text-[1rem]">How points work</h3>
        <p className="text-[0.85rem] text-body leading-relaxed mt-0 mb-3">
          At the end of each financial year, 75% of Reservo's net profit is shared among drivers based on points earned.
          The more points you earn, the bigger your share.
        </p>
        <div className="grid gap-2 text-[0.85rem]">
          <Rule label="Cancellation rate &lt; 1%" pts="+5 pts" colour="#2dd4a8" />
          <Rule label="Cancellation rate 1–2%" pts="+3 pts" colour="#2dd4a8" />
          <Rule label="Cancellation rate 2–3%" pts="+2 pts" colour="#fbbf24" />
          <Rule label="Cancellation rate 3–4%" pts="+1 pt" colour="#fbbf24" />
          <Rule label="Cancellation rate ≥ 4%" pts="0 pts" colour="#94a3b8" />
          <Rule label="Late cancel (within 2hr of pickup)" pts="-5 pts" colour="#ff6b6b" />
          <Rule label="Each successful referral" pts="+5 pts + $5" colour="#2dd4a8" />
        </div>
      </div>

      {/* Recent ledger */}
      <h3 className="mt-0 mb-3 text-[1rem]">Recent Activity</h3>
      {(!ledger || ledger.length === 0) ? (
        <div className="p-8 text-center text-muted bg-slate-50 rounded-xl">
          No activity yet this financial year.
        </div>
      ) : (
        <div className="grid gap-2">
          {ledger.map((entry) => (
            <LedgerEntry key={entry.id} entry={entry} />
          ))}
        </div>
      )}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}

function BreakdownCard({ label, value, sub, negative }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-slate-200">
      <div className="text-[0.75rem] text-body mb-1">{label}</div>
      <div className="text-[1.5rem] font-extrabold" style={{ color: negative ? '#ff6b6b' : '#0a1628' }}>{value}</div>
      {sub && <div className="text-[0.75rem] text-muted mt-1">{sub}</div>}
    </div>
  );
}

function Rule({ label, pts, colour }) {
  return (
    <div className="flex justify-between py-1.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
      <span className="text-body" dangerouslySetInnerHTML={{ __html: label }} />
      <span className="font-bold" style={{ color: colour }}>{pts}</span>
    </div>
  );
}

function LedgerEntry({ entry }) {
  const labels = {
    trip_completed: 'Trip completed',
    late_cancel_penalty: 'Late cancellation',
    referral_completed: 'Referral bonus',
  };
  const label = labels[entry.kind] || entry.kind;
  const positive = (entry.delta || 0) > 0;
  const ts = entry.createdAt ? new Date(entry.createdAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  let detail = '';
  if (entry.kind === 'trip_completed') {
    detail = `Cancellation rate at start: ${(entry.cancellationRatePercent || 0).toFixed(2)}%`;
  } else if (entry.kind === 'late_cancel_penalty') {
    detail = `${entry.bookingReferenceNumber || 'Booking'} · ${entry.hoursBeforePickup ?? '?'}hr before pickup`;
  } else if (entry.kind === 'referral_completed') {
    detail = `+$${(entry.bonusAUD || 0).toFixed(2)} added to wallet`;
  }

  return (
    <div className="flex justify-between items-center px-4 py-3 rounded-[10px] bg-white border border-slate-200">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[0.9rem] text-brand-dark">{label}</div>
        {detail && <div className="text-[0.78rem] text-muted mt-0.5">{detail}</div>}
        <div className="text-[0.72rem] text-muted mt-0.5">{ts}</div>
      </div>
      <div className="font-extrabold text-[1rem] whitespace-nowrap ml-3" style={{ color: positive ? '#2dd4a8' : '#ff6b6b' }}>
        {positive ? '+' : ''}{entry.delta} pts
      </div>
    </div>
  );
}